import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FormAlert } from '../components/auth/FormAlert'
import { apiClient } from '../lib/api/client'
import { useAuth } from '../lib/auth/auth-context'
import { requestPoTermsProblemCopy } from '../lib/auth/problem-messages'
import type { ProblemCopy } from '../lib/auth/problem-messages'
import { setAuthenticated } from '../lib/auth/session-store'

/**
 * Self-serve "request PO / Net terms" action + pending-status display (F0.8,
 * USP-38, PR-ONB-04/07). Mounted at `/account/billing`. The visible half of
 * `POST /onboarding/request-po-terms` (S0.11) — the Gate 2 UX spec's §2.4
 * proposed hybrid ("self-serve request, admin-authoritative action"); this
 * screen builds ONLY the self-serve request + pending half. The admin
 * "Qualify this account" action (§2.4 step 2/3) is E11 staff surface, not
 * this story.
 *
 * RD-08 / C15 (read this before touching this file): this screen has NO
 * client code path that sets or claims `qualified`. It only ever calls the
 * one request endpoint, which the server docs itself as
 * provisional|qualifying→qualifying ONLY — `qualified` is exclusively a
 * staff-gated, system-authored transition (the E11 saga). This component
 * only ever *reads* `qualified`/`suspended` off the server-derived session
 * or the request response; it never writes them.
 *
 * Guard: `auth.status === 'authenticated'` — deliberately ANY live member,
 * not `can(role, ...)`-gated, per the OQ-47 decision (human:jim, 2026-08-04)
 * recorded on the contract itself (`openapi.yaml`'s `requestPoTerms` has no
 * `x-required-capability` on purpose). This mirrors `<RequestToJoinPage>`'s
 * session-liveness-only guard shape, not `<ManageUsersPage>`'s
 * capability gate — see [[feedback_capability-gate-vs-preonboarding-guard]]
 * discipline: don't force a shared guard hook across screens with genuinely
 * different predicates.
 *
 * `lifecycleState` on `AccountContextView` is ADDITIVE + OPTIONAL (contract's
 * own doc comment) — absent on a deploy where the read port isn't wired. This
 * screen fails soft: an absent value is treated the same as `provisional`
 * (show the request action) rather than erroring or hiding the screen — the
 * POST itself is the authoritative check either way (a neutral 409 if the
 * account turns out not to be requestable).
 *
 * The 409 `po_terms_unavailable` is NEUTRAL by contract (already `qualified`
 * OR `suspended` map to the same response) — this screen does not attempt to
 * disambiguate which when driven by a fresh request in the same session that
 * DIDN'T already know the state; it only distinguishes them up front from
 * the session's own `lifecycleState` read (not a cross-account leak — it's
 * the caller's own account).
 */
export function BillingPage() {
  const auth = useAuth()

  const sessionLifecycleState =
    auth.status === 'authenticated' ? auth.session.accountContext.lifecycleState : undefined

  const [requestedState, setRequestedState] = useState<'qualifying' | undefined>(undefined)
  const [serverError, setServerError] = useState<ProblemCopy | undefined>()
  const [submitting, setSubmitting] = useState(false)

  const pendingHeadingRef = useRef<HTMLHeadingElement | null>(null)
  const alertRef = useRef<HTMLDivElement | null>(null)
  const wasRequestedRef = useRef(false)

  useEffect(() => {
    if (requestedState === 'qualifying' && !wasRequestedRef.current) {
      wasRequestedRef.current = true
      pendingHeadingRef.current?.focus()
    }
  }, [requestedState])

  useEffect(() => {
    if (serverError) {
      alertRef.current?.focus()
    }
  }, [serverError])

  async function handleRequest() {
    setServerError(undefined)
    setSubmitting(true)
    try {
      const { data, error } = await apiClient.POST('/onboarding/request-po-terms')
      if (data) {
        // The contract's own docstring: this endpoint can only ever return
        // `qualifying` (RD-08) — never `qualified`. No client-side branching
        // on any other value is written here on purpose.
        setRequestedState('qualifying')
        // USP-44 fix: sync the shared session store with the server's OWN
        // response so a remount (this component unmounts on route change,
        // `<AuthProvider>` does not — see this file's class doc comment)
        // reflects `qualifying` instead of falling back to the stale
        // `provisional` read from the last `GET /auth/session` probe. This
        // writes `data.lifecycleState` — the value the server itself just
        // returned for THIS request — not a client-fabricated value, so
        // RD-08 (no self-qualify) still holds. Deliberately calls
        // `setAuthenticated()` directly (same precedent as `<LoginPage>`)
        // rather than `auth.refresh()`: a second `GET /auth/session`
        // round-trip is redundant here — this response already IS the
        // authoritative state — and QA's repro test pins the request flow
        // to exactly one session probe.
        if (auth.status === 'authenticated') {
          setAuthenticated({
            ...auth.session,
            accountContext: {
              ...auth.session.accountContext,
              lifecycleState: data.lifecycleState,
            },
          })
        }
        return
      }
      if (error?.status === 401) {
        // A genuinely-authenticated session expiring mid-use is handled
        // globally: `/onboarding/request-po-terms` is NOT in
        // `AUTH_SURFACE_PATHS`, so `authMiddleware`'s `invalidateSession()`
        // already flipped `auth.status` to `unauthenticated` before this
        // branch runs — this component's own re-render shows the sign-in
        // dead-end below, matching `<ManageUsersPage>`'s documented
        // rationale for NOT special-casing 401 here.
        return
      }
      setServerError(requestPoTermsProblemCopy(error?.type, error?.detail))
    } catch {
      setServerError({ title: 'Something went wrong', detail: 'Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  // The initial session probe is still in flight. `loading` is not
  // `unauthenticated`: showing the sign-in dead-end now would flash it at an
  // already-signed-in member before swapping to content (same rule as
  // <RequireAuth>).
  if (auth.status === 'loading') {
    return null
  }

  if (auth.status !== 'authenticated') {
    return (
      <section className="auth-page">
        <h1 className="display-heading">Sign in to manage billing</h1>
        <p>We need to confirm it&rsquo;s you before showing billing and payment terms.</p>
        <p>
          <Link to="/login">Sign in</Link>
        </p>
      </section>
    )
  }

  const effectiveState = requestedState ?? sessionLifecycleState

  if (effectiveState === 'qualified') {
    return (
      <section className="auth-page">
        <h1 className="display-heading">PO / Net terms are approved</h1>
        <p>Your account is approved for PO and Net Terms payment options at checkout.</p>
      </section>
    )
  }

  if (effectiveState === 'suspended') {
    return (
      <section className="auth-page">
        <h1 className="display-heading">Billing is unavailable right now</h1>
        <p>Contact support for help with your account&rsquo;s billing options.</p>
        <p>
          <a href="mailto:support@fictiv.com">Contact support</a>
        </p>
      </section>
    )
  }

  if (effectiveState === 'qualifying') {
    return (
      <section className="auth-page">
        <h1
          className="display-heading"
          ref={pendingHeadingRef}
          tabIndex={-1}
          id="po-terms-pending-heading"
        >
          Your request is pending
        </h1>
        <output className="status-banner" aria-live="polite">
          <span className="status-banner-icon" aria-hidden="true">
            &#9689;
          </span>
          <div>
            <p className="status-banner-title">Pending approval &middot; PO / Net Terms</p>
            <p className="status-banner-detail">
              We&rsquo;ve received your request. An account approval is required before PO and Net
              Terms payment options unlock at checkout — this is never automatic. We&rsquo;ll email
              you once a decision is made.
            </p>
          </div>
        </output>
      </section>
    )
  }

  // provisional, or lifecycleState absent (fail-soft) — offer the request.
  return (
    <section className="auth-page">
      <h1 className="display-heading">Billing &amp; payment terms</h1>
      <p>
        PO and Net Terms require account approval. Card and ACH payment are available today —
        request PO / Net Terms to unlock those options for future orders.
      </p>
      {serverError && (
        <FormAlert ref={alertRef} title={serverError.title} detail={serverError.detail} />
      )}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void handleRequest()
        }}
      >
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Requesting…' : 'Request PO / Net Terms'}
          </button>
        </div>
      </form>
    </section>
  )
}
